import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, ShieldCheck, ChevronDown, Search, Check, Users, Clock } from 'lucide-react';
import { pieApi, piePersona, pieCoachSession } from './pieApi';

/**
 * PIEPersonaChip — sticky strip below the PIE nav. Names the active
 * persona; clicking opens a popover with two sections:
 *
 *   1. Switch persona — searchable list of all personas (instant
 *      switch, no round-trip through the landing page). Replaces the
 *      old super-admin-only "Switch persona" button.
 *
 *   2. Data sources — auditable list of where each number comes from
 *      (kept; this is the CFO trust surface).
 */
export default function PIEPersonaChip() {
  const navigate = useNavigate();
  const location = useLocation();
  const [persona, setPersona] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('personas'); // 'personas' | 'sources'
  const [personas, setPersonas] = useState(null);
  const [sources, setSources] = useState(null);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [loadingSources, setLoadingSources] = useState(false);
  const [search, setSearch] = useState('');
  const popoverRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    pieApi
      .activePersona()
      .then((p) => { if (mounted) setPersona(p); })
      .catch(() => { if (mounted) setPersona(null); });
    return () => { mounted = false; };
  }, [location.pathname]);

  // Lazy-load each tab's data only when first opened.
  useEffect(() => {
    if (!open) return;
    if (tab === 'personas' && !personas && !loadingPersonas) {
      setLoadingPersonas(true);
      pieApi
        .listPersonas()
        .then((d) => setPersonas(d.personas || []))
        .catch(() => setPersonas([]))
        .finally(() => setLoadingPersonas(false));
    }
    if (tab === 'sources' && !sources && !loadingSources) {
      setLoadingSources(true);
      pieApi
        .dataSources()
        .then(setSources)
        .catch(() => setSources({ sources: [] }))
        .finally(() => setLoadingSources(false));
    }
  }, [open, tab, personas, sources, loadingPersonas, loadingSources]);

  // Auto-focus search when persona tab opens.
  useEffect(() => {
    if (open && tab === 'personas') {
      // Defer until the popover is painted.
      const t = setTimeout(() => searchRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, tab]);

  // Close on outside click + ESC.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onPickPersona = (id) => {
    if (id === persona?.id) {
      setOpen(false);
      return;
    }
    // Track the persona we're leaving in the "recently switched" history
    // so it appears at the top of the next switcher panel for fast
    // back-and-forth comparison (e.g., Hana ↔ Jules ↔ Marcus). Capped at
    // 3 entries, deduped, persisted in localStorage.
    if (persona?.id) {
      try {
        const raw = localStorage.getItem('pie.recent_personas');
        const prev = raw ? JSON.parse(raw) : [];
        const next = [persona.id, ...prev.filter((x) => x !== persona.id && x !== id)].slice(0, 3);
        localStorage.setItem('pie.recent_personas', JSON.stringify(next));
      } catch (_) { /* localStorage unavailable */ }
    }
    piePersona.set(id);
    pieCoachSession.clear();
    setOpen(false);
    // Hard reload to refresh every page-level fetch (snapshot, insights, team)
    // without us having to thread state through every PIE page.
    window.location.reload();
  };

  /** Last 3 personas the user has switched away from. Pulled from
   *  localStorage on every popover open so it stays fresh. Filtered
   *  against the loaded personas list to drop any stale ids and to
   *  exclude the currently-active persona. */
  const recentPersonas = useMemo(() => {
    if (!personas || !open || search.trim()) return [];
    let ids = [];
    try {
      const raw = localStorage.getItem('pie.recent_personas');
      ids = raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
    const byId = new Map(personas.map((p) => [p.id, p]));
    return ids
      .filter((id) => id !== persona?.id && byId.has(id))
      .map((id) => byId.get(id))
      .slice(0, 3);
  }, [personas, persona, open, search]);

  const filteredPersonas = useMemo(() => {
    if (!personas) return [];
    const q = search.trim().toLowerCase();
    if (!q) return personas;
    return personas.filter((p) =>
      [p.name, p.role_label, p.archetype_label].filter(Boolean).some((s) => s.toLowerCase().includes(q)),
    );
  }, [personas, search]);

  /** Three "most-different" managers surfaced at the top of the
   *  personas tab as a quick-pick row. We pick one manager from each
   *  of the most varied archetypes (high_performer, plateau,
   *  expert_coasting / new_hire / struggling — whatever exists). The
   *  goal is to let a first-time user discover the team view in one
   *  click instead of scrolling the alphabetical list. */
  const managerQuickPicks = useMemo(() => {
    if (!personas || search.trim()) return [];
    const managers = personas.filter((p) => p.is_manager);
    if (managers.length < 2) return [];
    const seen = new Set();
    const picks = [];
    for (const m of managers) {
      const key = m.archetype || 'unknown';
      if (seen.has(key)) continue;
      seen.add(key);
      picks.push(m);
      if (picks.length === 3) break;
    }
    // If we couldn't find three different archetypes, top up from
    // the remaining managers so the row always renders 3 cards.
    if (picks.length < 3) {
      for (const m of managers) {
        if (picks.find((p) => p.id === m.id)) continue;
        picks.push(m);
        if (picks.length === 3) break;
      }
    }
    return picks;
  }, [personas, search]);

  if (location.pathname === '/mirror') return null;
  if (!persona) return null;

  return (
    <div className="pie-persona-chip-bar" data-testid="pie-persona-chip">
      <div className="pie-persona-chip-inner" ref={popoverRef}>
        <button
          type="button"
          className="pie-persona-chip-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="pie-persona-chip-trigger"
        >
          <span className="pie-persona-chip-avatar">
            <User size={13} />
          </span>
          <span className="pie-persona-chip-name">{persona.name}</span>
          <span className="pie-persona-chip-sep">·</span>
          <span className="pie-persona-chip-role">{persona.role_label}</span>
          <span
            className="pie-persona-chip-archetype"
            data-tooltip={persona.archetype_tagline || ''}
            data-testid="pie-persona-chip-archetype"
          >
            {persona.archetype_label}
          </span>
          <ChevronDown
            size={12}
            className="pie-persona-chip-caret"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>

        {open && (
          <div className="pie-persona-chip-popover" data-testid="pie-persona-chip-popover">
            <div className="pie-persona-chip-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'personas'}
                className={`pie-persona-chip-tab ${tab === 'personas' ? 'pie-persona-chip-tab-active' : ''}`}
                onClick={() => setTab('personas')}
                data-testid="pie-persona-chip-tab-personas"
              >
                Switch persona
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'sources'}
                className={`pie-persona-chip-tab ${tab === 'sources' ? 'pie-persona-chip-tab-active' : ''}`}
                onClick={() => setTab('sources')}
                data-testid="pie-persona-chip-tab-sources"
              >
                Data sources
              </button>
            </div>

            {tab === 'personas' && (
              <div className="pie-persona-chip-personas" data-testid="pie-persona-chip-personas">
                <div className="pie-persona-chip-search">
                  <Search size={13} aria-hidden />
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search personas…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="pie-persona-chip-search-input"
                  />
                </div>
                {loadingPersonas && (
                  <div className="pie-persona-chip-loading">Loading personas…</div>
                )}
                {!loadingPersonas && filteredPersonas.length === 0 && (
                  <div className="pie-persona-chip-loading">No personas match.</div>
                )}
                {recentPersonas.length > 0 && (
                  <div className="pie-persona-chip-quickpicks" data-testid="pie-persona-chip-recent">
                    <div className="pie-persona-chip-quickpicks-label">
                      <Clock size={11} aria-hidden /> Recently switched
                    </div>
                    <div
                      className="pie-persona-chip-quickpicks-row"
                      style={{ gridTemplateColumns: `repeat(${recentPersonas.length}, 1fr)` }}
                    >
                      {recentPersonas.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="pie-persona-chip-quickpick"
                          onClick={() => onPickPersona(p.id)}
                          data-testid={`pie-persona-chip-recent-${p.id}`}
                          title={`${p.name} · ${p.role_label} · ${p.archetype_label}`}
                        >
                          <span className="pie-persona-chip-quickpick-name">{p.name.split(' ')[0]}</span>
                          <span className="pie-persona-chip-quickpick-arch">
                            {p.archetype_label}{p.is_manager ? ' · MGR' : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {managerQuickPicks.length > 0 && (
                  <div className="pie-persona-chip-quickpicks" data-testid="pie-persona-chip-quickpicks">
                    <div className="pie-persona-chip-quickpicks-label">
                      <Users size={11} aria-hidden /> Try a manager
                    </div>
                    <div className="pie-persona-chip-quickpicks-row">
                      {managerQuickPicks.map((m) => {
                        const active = m.id === persona.id;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            className={`pie-persona-chip-quickpick ${active ? 'pie-persona-chip-quickpick-active' : ''}`}
                            onClick={() => onPickPersona(m.id)}
                            data-testid={`pie-persona-chip-quickpick-${m.id}`}
                            title={`${m.name} · ${m.role_label} · ${m.archetype_label}`}
                          >
                            <span className="pie-persona-chip-quickpick-name">{m.name.split(' ')[0]}</span>
                            <span className="pie-persona-chip-quickpick-arch">{m.archetype_label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <ul className="pie-persona-chip-list" role="listbox">
                  {filteredPersonas.map((p) => {
                    const active = p.id === persona.id;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={`pie-persona-chip-row ${active ? 'pie-persona-chip-row-active' : ''}`}
                          onClick={() => onPickPersona(p.id)}
                          data-testid={`pie-persona-chip-row-${p.id}`}
                        >
                          <span className="pie-persona-chip-row-name">
                            {p.name}
                            {p.is_manager && (
                              <span className="pie-persona-chip-row-mgr">manager</span>
                            )}
                          </span>
                          <span className="pie-persona-chip-row-meta">
                            {p.role_label} · {p.archetype_label}
                          </span>
                          {active && (
                            <Check size={13} className="pie-persona-chip-row-check" aria-hidden />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {tab === 'sources' && (
              <div className="pie-data-sources-popover-body" data-testid="pie-data-sources-popover">
                <div className="pie-data-sources-header">
                  <ShieldCheck size={14} aria-hidden style={{ color: 'var(--pie-accent)' }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--pie-text)' }}>
                      Where each number comes from
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--pie-text-muted)' }}>
                      Every figure is auditable to its underlying source.
                    </div>
                  </div>
                </div>
                {loadingSources && (
                  <div style={{ padding: '20px 0', textAlign: 'center' }}>
                    <span className="pie-loader"><span /><span /><span /></span>
                  </div>
                )}
                {sources?.sources?.map((s) => (
                  <div key={s.key} className="pie-data-source-row" data-testid={`pie-data-source-${s.key}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--pie-text)' }}>
                        {s.label}
                      </span>
                      <span className="pie-mono" style={{ fontSize: 13, color: 'var(--pie-accent)', fontWeight: 600 }}>
                        {s.value_pct}{s.key === 'personal_lei' ? '/100' : '%'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--pie-text-secondary)', marginTop: 4 }}>
                      {s.source}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--pie-text-muted)', marginTop: 4 }}>
                      Last refreshed: <span className="pie-mono">{relativeDays(s.last_refreshed)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function relativeDays(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.round(ms / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}
